import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wrench, ShieldCheck, Clock, Cpu, FileSearch, HeartPulse, Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { useSettings } from "@/lib/settings";
import { mediaUrl } from "@/lib/media";
import { Section, SectionTitle } from "@/components/Section";

const ICON_LIST = [Wrench, ShieldCheck, HeartPulse, Cpu, FileSearch, Clock];
const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
const FALLBACK_IMG = "https://static.prod-images.emergentagent.com/jobs/06925219-56b3-4645-9e86-564535697c34/images/2e53b5613cd0f5a5360e107dfc70eb13989aeb2a6ae245d27dfe640ebaa1385f.png";

export default function Engineers() {
  const { settings } = useSettings();
  const services = settings?.engineers_services && settings.engineers_services.length > 0
    ? settings.engineers_services
    : [
        { title: "Breakdown Repair", desc: "24x7 emergency response across Bihar." },
        { title: "Preventive Maintenance", desc: "AMC contracts for predictable uptime." },
        { title: "Engine Overhaul", desc: "Complete rebuilds with OEM-grade parts." },
        { title: "Hydraulic System Service", desc: "Pump, motor and cylinder service." },
        { title: "Electrical & ECU Diagnostics", desc: "Modern CRDi, BS-IV/VI diagnostics." },
        { title: "Equipment Health Audit", desc: "Pre-purchase and fleet health audits." },
      ];

  const heroImg = settings?.engineers_hero_image ? mediaUrl(settings.engineers_hero_image) : FALLBACK_IMG;
  const aboutImg = settings?.engineers_image ? mediaUrl(settings.engineers_image) : heroImg;

  return (
    <>
      <section className="relative bg-[#1A1A1A] text-white overflow-hidden noise-overlay" data-testid="engineers-hero">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="w-full h-full object-cover object-center opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A] via-[#1A1A1A]/85 to-[#1A1A1A]/40" />
          <div className="absolute inset-0 grid-bg opacity-30" />
        </div>
        <Section className="relative py-24 lg:py-32 max-w-7xl">
          <div className="label-eyebrow mb-4">{settings?.engineers_eyebrow || "TARA ENGINEERS"}</div>
          <h1 className="heading-xl text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl">
            {(() => {
              const t = settings?.engineers_title || "MULTI-BRAND SERVICE ENGINEERS. ON CALL. ON SITE.";
              const parts = t.split(".");
              // Highlight the LAST non-empty sentence segment in saffron
              const last = [...parts].reverse().find((p) => p.trim());
              return parts.map((seg, i) => {
                if (!seg.trim()) return <span key={i}>{i < parts.length - 1 ? "." : ""}</span>;
                const isLast = seg === last;
                return (
                  <span key={i} className={isLast ? "text-[#E65C00]" : ""}>{seg}{i < parts.length - 1 ? "." : ""}</span>
                );
              });
            })()}
          </h1>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl leading-relaxed">
            {settings?.engineers_subtitle || "Beyond our Bull Machines dealership, our certified engineering team services equipment from every major OEM."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary" data-testid="engineers-cta-book">
              <Phone className="w-4 h-4" /> Book a Service Visit
            </Link>
            <a href={`tel:${(settings?.phone || "").replace(/\s+/g, "")}`} className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#1A1A1A]">
              Call Engineers Desk
            </a>
          </div>
        </Section>
      </section>

      {/* About */}
      <Section className="py-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fadeUp} className="relative">
          <img src={aboutImg} alt="Service engineers at work" className="w-full h-[480px] object-cover" />
          <div className="absolute -bottom-6 -left-6 bg-[#FFCC00] p-5 border-4 border-[#1A1A1A] max-w-[260px]">
            <Wrench className="w-7 h-7 text-[#1A1A1A]" />
            <div className="font-heading font-black text-[#1A1A1A] text-sm mt-3 uppercase leading-tight">Factory-Trained · Multi-Brand Certified</div>
          </div>
        </motion.div>
        <motion.div {...fadeUp}>
          <SectionTitle eyebrow="ABOUT THE VERTICAL" title="ENGINEERED FOR UPTIME." />
          <p className="mt-6 text-gray-700 leading-relaxed text-base sm:text-lg max-w-lg">
            {settings?.engineers_about || "Tara Engineers is our dedicated technical services vertical. Our field engineers are factory-trained, multi-brand certified, and equipped with diagnostic tools, OEM-grade spares and a service van fleet that reaches your job site within hours — not days."}
          </p>
          <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-lg">
            {["All-brand servicing", "OEM-grade spares", "2-hour ETA in Bihar", "AMC contracts", "Pan-India dispatch", "Genuine parts only"].map((p) => (
              <div key={p} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[#E65C00] mt-0.5 shrink-0" />
                <span className="text-gray-800 font-medium">{p}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* Services bento */}
      <section className="bg-[#0F0F0F] text-white py-20 noise-overlay">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <Section className="relative">
          <SectionTitle eyebrow="WHAT WE FIX" title={<span className="text-white">SIX VERTICALS. ZERO DOWNTIME.</span>} sub={<span className="text-gray-400">Whatever's parked on your site, we can service it.</span>} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px mt-12 bg-white/10">
            {services.map((s, i) => {
              const Icon = ICON_LIST[i % ICON_LIST.length];
              return (
                <motion.div key={`${s.title}-${i}`} {...fadeUp} transition={{ duration: 0.5, delay: i*0.05 }}
                  className="bg-[#1A1A1A] p-8 group hover:bg-[#E65C00] transition-colors duration-300"
                  data-testid={`engineers-service-${i}`}
                >
                  <Icon className="w-9 h-9 text-[#E65C00] group-hover:text-white" />
                  <h3 className="font-heading font-black text-lg uppercase mt-6 leading-tight">{s.title}</h3>
                  <p className="text-sm text-gray-400 group-hover:text-white/90 mt-3 leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </section>

      <section className="bg-[#FFCC00] py-14">
        <Section>
          <div className="grid md:grid-cols-2 items-center gap-8">
            <div>
              <h2 className="heading-xl text-3xl sm:text-4xl text-[#1A1A1A]">MACHINE DOWN?<br />CALL OUR ENGINEERS NOW.</h2>
            </div>
            <div className="md:text-right">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white font-heading font-black uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-black transition-colors">
                Book Service Visit <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Section>
      </section>
    </>
  );
}
